/**
 * BatchGeoTableMap is a JavaScript library that integrates BatchGeoTable with
 * Google Maps as rendered by BatchGeo. This library combines all the
 * BatchGeoTable plugins needed to display a BatchGeoTable on the map pages and
 * wires them to that specific page.
 */
(function (window) {
  /**
   * This is the BatchGeoTableMap constructor. It takes an options hash with some
   * required options:
   *
   * - map: Your Google Maps Map() instance
   * - markers: An array/collection of BatchGeo marker instances
   * - data: A BatchGeo data object that contains all the map and marker data
   *
   * @param {object} options Object hash of the options listed above
   *
   * @example
   * var batchgeoTableMap = new BatchGeoTableMap({
   *   map: map,
   *   markers: markers,
   *   data: per
   * });
   *
   */
  function BatchGeoTableMap(options) {
    var elements = this._cacheElements();
    this.map = options.map;
    this.markers = options.markers;
    this.data = options.data;
    this.labelType = options.data.labeltype_sel;
    this.batchGeoTable = new BatchGeoTable({
      data: this.data,
      container: elements.dataView[0],
      plugins: [
        window.HeaderSort,
        window.RowSelect,
        window.RowLinkifier,
        window.RowFilter,
      ],
    });

    this.batchGeoPrintTable = new window.BatchGeoPrintTable(this);
    this.batchGeoCopyMachine = window.BatchGeoCopyMachine();
    this.measurePolylines =
      BatchGeoStore.getState().BatchGeoOptimalRouteToolbar.polylines; //new BatchGeoMarkerMeasurePolylines();

    this.listForDispatch = [];

    this.markerLabelColumnGenerator = window.MarkerLabelColumnGenerator(
      this.batchGeoTable,
    );
    this.distanceColumnGenerator = window.DistanceColumnGenerator(
      this.batchGeoTable,
    );
    this.destinationColumnGenerator = window.DestinationColumnGenerator(
      this.batchGeoTable,
    );
    this.optimalDistanceColumnGenerator = window.OptimalDistanceColumnGenerator(
      this.batchGeoTable,
    );
    this.tableContextMenu = window.BatchGeoTableContextMenu().init();

    this._initRowSelector();
    this._initTableResizer();
    this._initSelectAllListener();
    this._initFilterListener();
    this._initBgSizeListener();
    this._initPaginationListener();
    // this._initDrawingListener();

    // Render the table last after all listeners have been setup
    this.batchGeoTable.render();

    return this;
  }

  BatchGeoTableMap.prototype.getListForDispatch = function () {
    return this.listForDispatch;
  };

  BatchGeoTableMap.prototype.addToListForDispatch = function (polyline) {
    this.listForDispatch.push(polyline);
  };

  BatchGeoTableMap.prototype.clearListForDispatch = function () {
    //remove association between polyline object and map object
    this.listForDispatch.forEach((mapItem) => {
      mapItem.polyline.setMap(null);
    });

    //clear the object after they have been removed from the map
    this.listForDispatch = [];

    //return the empty list of polylines
    return this.listForDispatch;
  };
  BatchGeoTableMap.prototype.getMeasurePolylines = function () {
    return this.measurePolylines;
  };

  /**
   * Makes it so when you click on something in the dataview and then press
   * cmd/ctrl+a it will select all rows.
   *
   * @return {undefined}
   */
  BatchGeoTableMap.prototype._initSelectAllListener = function () {
    var self = this;
    // On keydown if the user is pressing cmd/ctrl + the letter `a` we call
    // select all
    $(document.body).on("keydown", function (event) {
      if ((event.metaKey || event.ctrlKey) && event.which == 65) {
        if (
          ["input", "textarea"].indexOf(event.target.nodeName.toLowerCase()) ===
          -1
        ) {
          self.batchGeoTable.RowSelect.selectAll();
          return false;
        }
      }
    });
  };

  BatchGeoTableMap.prototype.removeSelectAllListener = function () {
    $(document.body).off("keydown");
  };

  /**
   * Listens for any updates to filters (like searching or clicking a marker
   * group in the legend) and then filters down the table based on that to
   * match the markers shown.
   *
   * It also handles cached filters upon later reloads.
   *
   * @return {undefined}
   */
  BatchGeoTableMap.prototype._initFilterListener = function () {
    var self = this;
    var rowFilter = this.batchGeoTable.RowFilter;

    var onFilteredChange = function (state) {
      var filteredMarkers = state.MarkerLegend.filteredMarkers;
      // If there are markers in the array then filter the data, we don't trigger the
      // table render until the very end
      filteredMarkers.length
        ? rowFilter.filterByIds(filteredMarkers)
        : rowFilter.reset();
      // Reapply marker labels if needed
      self.markerLabelColumnGenerator.refreshMarkerLabelData(filteredMarkers);
      // Trigger table update
      rowFilter.update();
    };

    BatchGeoStore.addListener(
      function (state) {
        return state;
      },
      function (nextState, prevState) {
        return (
          nextState.MarkerLegend.filteredMarkers.toString() !==
          prevState.MarkerLegend.filteredMarkers.toString()
        );
      },
      onFilteredChange,
    );

    if (BatchGeoStore.getState().MarkerLegend.isFiltered) {
      var preFilterTrigger = function () {
        onFilteredChange(BatchGeoStore.getState());
        self.batchGeoTable.removeListener("rowsbuilt", preFilterTrigger);
      };
      self.batchGeoTable.addListener("rowsbuilt", preFilterTrigger);
    }
  };

  /**
   * Sets up the listener that handles all the table sizing whenever the bgsize
   * event is called in the app.
   *
   * @return {undefined} Nothing...
   */
  BatchGeoTableMap.prototype._initBgSizeListener = function () {
    var self = this;
    $(window).on("batchgeo.bgsize", function () {
      self.batchGeoTable.resize();
      self.updateTableHeight();
    });
  };

  /**
   * Sets up the listener that handles row selection when the map marker info
   * box pagination is clicked to cycle through a cluster of points.
   *
   * @return {undefined} Nothing...
   */
  BatchGeoTableMap.prototype._initPaginationListener = function () {
    var self = this;
    BatchGeoStore.addListener(
      (state) => {
        return state.MapsMarkersManager;
      },
      (next, prev) => {
        return next.currentPaginationNumber !== prev.currentPaginationNumber;
      },
      (next) => {
        var page = next.paginationCluster[next.currentPaginationNumber];
        self.batchGeoTable.RowSelect.deselectAll();

        if (!page) {
          return;
        }

        const r = page.marker.r;
        self.batchGeoTable.RowSelect.select(r, { infoBox: false });
        self.batchGeoTable.scrollToRow(r);
      },
    );
  };

  /**
   * Caches all commonly used elements. Can be called later if you want to make
   * sure you have the most up to date versions of the element.
   *
   * Note: Use `this._getElementCache()` to get this cache later without needing
   * to reset it.
   *
   * @return {object} Returns an object of all the cached elements
   */
  BatchGeoTableMap.prototype._cacheElements = function () {
    // Caching commonly looked up elements
    return (this._elementCache = {
      mapWrap: $("#mapWrap"),
      mapDiv: $("#mapDiv"),
      table: $(".clusterize"),
      scroller: $(".clusterize-scroll"),
      dataView: $("#dataView"),
      printView: $("#printView"),
      fauxTableHeader: $(".clusterize-faux-thead"),
    });
  };

  /**
   * Returns the cached elements created from `this._cacheElements`;
   *
   * @return {object} Returns an object of all the cached elements
   */
  BatchGeoTableMap.prototype._getElementCache = function () {
    return this._elementCache;
  };

  /**
   * Resets the table height to fill the proper area without overflow.
   */
  BatchGeoTableMap.prototype.updateTableHeight = function () {
    var els = this._cacheElements();

    var windowHeight = $(window).outerHeight();
    var mapWrapHeight = els.mapWrap.outerHeight();
    var tableHeaderHeight = els.fauxTableHeader.outerHeight();
    var windowWidth = $(window).width();

    // Size scroll for appropriate device window size
    if (windowWidth > 600) {
      els.scroller.height(windowHeight - mapWrapHeight - tableHeaderHeight);
    } else {
      els.scroller.height("calc(100% - 60px)");
    }
  };

  /**
   * Preps BatchGeoTable for the row selection events
   *
   * @return {object} Returns the BatchGeoTableMap instance
   */
  BatchGeoTableMap.prototype._initRowSelector = function () {
    var self = this;

    // Goes through and highlights the rows based on IDs. We loop through the
    // elements and NOT the data because there will only ever be a couple
    // hundred DOM nodes but there could be 10s of thousands of rows of data
    var highlightRows = function (rowIds) {
      const rowsById = rowIds.reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {});

      self
        ._getElementCache()
        .dataView.find("[data-id]")
        .each(function (index, el) {
          var domId = $(el).data("id");
          $(el).toggleClass("selected", Boolean(rowsById[domId]));
        });
    };

    // Since Clusterize wipes out the DOM when a new cluster is loaded we need
    // to rehighlight the rows whenever the table is updated.
    this.batchGeoTable.addListener("rowupdate", function () {
      highlightRows(BatchGeoStore.getState().RowSelect.selection);
    });

    BatchGeoStore.addListener(
      // Listen for all of RowSelect because we want the `.options` prop too
      function (state) {
        return state.RowSelect;
      },
      function (nextRowState, prevRowState) {
        return (
          nextRowState.selection.toString() != prevRowState.selection.toString()
        );
      },
      function (nextRowState, _prevRowState) {
        if (nextRowState.selectedFromDrawing) {
          const markerIds = nextRowState.selection;
          // Get the current table visibilty
          const tableVisibility =
            BatchGeoStore.getState().BatchGeoTableMap.visibility;
          // If no markers are selected deselect them and check if we need to close the table and exit
          if (!markerIds.length) {
            // If the table is visible but the app isn't meant to display it we'll close it
            if (
              tableVisibility &&
              !BatchGeoStore.getState().App.shouldRenderTable
            ) {
              BatchGeoStore.dispatch({
                type: "TABLE_MAP_VISIBILITY",
                visibility: false,
                shouldForceRenderTable: false,
              });

              window.batchGeoMap.bgSize(true);
            }

            return self.batchGeoTable.RowSelect.deselectAll();
          }

          // If the table is not visible and we've selected markers, add a listener
          // to open the table once it has been rebuilt
          if (!tableVisibility) {
            const dataViewDisplayTrigger = function () {
              BatchGeoStore.dispatch([
                {
                  type: "TABLE_MAP_VISIBILITY",
                  visibility: true,
                  shouldForceRenderTable: true,
                },
                {
                  type: "FEATURE_TOGGLE",
                  feature: "shouldRenderTable",
                },
              ]);

              window.batchGeoMap.bgSize(true);
              self.batchGeoTable.removeListener(
                "rowsbuilt",
                dataViewDisplayTrigger,
              );
            };

            self.batchGeoTable.addListener("rowsbuilt", dataViewDisplayTrigger);
          }

          // Make sure to use getDisplayData() so that the ordering is based on the
          // current sorting order, NOT by ID
          const rows = self.batchGeoTable.getDisplayData();

          // Go through the data and create a new array with 2 children arrays that
          // are selected and notSelected and move the rows into one of those two
          // rows and then concat them into one array.
          const result = rows.reduce(
            function (result, row) {
              var arrayName =
                markerIds.indexOf(row.id) !== -1 ? "selected" : "notSelected";
              result[arrayName].push(row);
              return result;
            },
            {
              selected: [],
              notSelected: [],
            },
          );

          const sortedRows = result.selected.concat(result.notSelected);

          // Update the sort and once that is completely finished and the DOM is
          // done being manipulated update the selections
          self.batchGeoTable.updateRowDisplay(sortedRows, function () {
            self.batchGeoTable.RowSelect.select(markerIds, {
              fitMarkers: false,
              infoBox: false,
            });
            self.batchGeoTable.scrollToRow(sortedRows[0].id);
          });
        }

        const globalState = BatchGeoStore.getState();
        const rowIds = nextRowState.selection;
        const shouldRefreshClarker = globalState.Cluster.enabled;
        const options = Object.assign(nextRowState.options, {
          shouldRefreshClarker: shouldRefreshClarker,
        });

        // Now highlight the new rows, and handle the newly selected markers
        highlightRows(rowIds);
        self._onRowSelect(rowIds, options);
      },
    );

    return this;
  };

  /**
   * Handles row selections on BatchGeoTable by opening the info windows when
   * only one is selected and fading out unselected markers and fitting all the
   * selected markers within the visible map area
   *
   * @param  {arrow} rows An array of BatchGeoTable data items (returned in rowselect events)
   * @return {object} Returns the BatchGeoTableMap instance
   */
  BatchGeoTableMap.prototype._onRowSelect = function (rowIds, options) {
    var self = this;
    var isHeatmap = BatchGeoStore.getState().HeatMap.enabled;
    options = options || {};
    var settings = $.extend(
      {
        // if you want an infobox displayed on marker select
        infoBox: true,
        // if you want to fit the markers in view
        fitMarkers: true,
        // If either the previous selection or next selection was greater
        // than one we should refresh the clusters
        shouldRefreshClarker: true,
        fromMarkerClick: false,
      },
      options,
    );

    var googleMarkers = this.getGoogleMarkersFromRows(rowIds);

    // Whenever we (de)select more than 1 row we need update the clusters
    // but skip if heatmap is on
    if (!isHeatmap && settings.shouldRefreshClarker) {
      this._updateOrResetClarkerMarkers();
    }

    // If there's no markers selected there's nothing to fit
    if (
      settings.fitMarkers &&
      googleMarkers.length &&
      !settings.fromMarkerClick
    ) {
      if (googleMarkers.length === 1 && !BatchGeoDevices.is("mobile")) {
        var marker = googleMarkers[0];
        if (!this.map.getBounds().contains(marker.getPosition())) {
          // move to view the marker
          this.map.setCenter(marker.getPosition());
        }
      }
      // if there's more than 1 we will fit all of them which could change the zoom
      else {
        // close any open non-pinned info windows
        window.batchGeoGoogleMapsMarker.MarkerInfoWindow.close();
        if (window.per.map_render_type.type !== "choropleth") {
          this.fitMarkers(googleMarkers);
        }
      }
    }

    // After a row select we prepare for a cmd/ctrl+c copy by setting the copy
    // contents to the data of the selected rows. We only want to do this for
    // desktop browsers though because, for one, keyboard shortcuts wont even work
    // and two because of the mechanics of how CopyMachine works it will trigger
    // the keyboard to open on Android. See ticket #1184
    if (!BatchGeoDevices.is("mobile") && !window.per.disable_exports) {
      this.batchGeoCopyMachine.copyContents(function () {
        return window.TableExporter({
          columns: self.batchGeoTable.data.columns,
          rows: self.batchGeoTable.getRowData(rowIds),
          sortAgainst: self.batchGeoTable.getDisplayData(),
        });
      });
    }

    let optimalPairs =
      BatchGeoStore.getState().BatchGeoOptimalRouteToolbar.polylineData;

    if (optimalPairs.length > 0) {
      for (let i = 0; i < rowIds.length; i++) {
        //check if the row id is a starting location for a polyline
        for (let item = 0; item < optimalPairs.length; item++) {
          if (rowIds[i] == optimalPairs[item].startId) {
            var map = window.batchGeoGoogleMapsManager.getMap();
            let polyline = new google.maps.Polyline({
              map: map,
              path: [optimalPairs[item].start, optimalPairs[item].end],
              strokeColor: optimalPairs[item].color || "green", //'#4285F4',
              strokeOpacity: 0.8,
            });
            this.listForDispatch.push({
              polyline: polyline,
              startId: optimalPairs[item].startId,
            });
          }
        }
      }
    }
    // clear if no rows selected (not deleleted, just cleared from the map)
    if (rowIds.length == 0) {
      if (this.listForDispatch.length > 0) {
        this.listForDispatch.forEach((polyline) => {
          polyline.polyline.setMap(null);
        });
      }
    }
    return this;
  };

  BatchGeoTableMap.prototype.getDisplayDataBySelectedState = function () {
    const selectedMarkerById =
      BatchGeoStore.getState().RowSelect.selection.reduce((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {});
    // Make sure to use getDisplayData() so that the ordering is based on the
    // current sorting order, NOT by ID
    const rows = this.batchGeoTable.getDisplayData();

    // Go through the data and create a new array with 2 children arrays that
    // are selected and notSelected and move the rows into one of those two
    // rows
    return rows.reduce(
      function (result, row) {
        var arrayName = selectedMarkerById[row.id] ? "selected" : "notSelected";
        result[arrayName].push(row);
        return result;
      },
      {
        selected: [],
        notSelected: [],
      },
    );
  };

  BatchGeoTableMap.prototype.updateSelectedOrder = function (
    newSelectedOrderByIndex,
  ) {
    const rowSelect = window.batchGeoTableMap.batchGeoTable.RowSelect;
    const { selected, notSelected } = this.getDisplayDataBySelectedState();

    if (selected.length !== newSelectedOrderByIndex.length) {
      console.warn("Selected length does not match new order length");
      return false;
    }

    const selectedRowIds = rowSelect.getSelectedRows().map((item) => item.id);
    const newSelectedOrder = newSelectedOrderByIndex.map(
      (index) => selectedRowIds[index],
    );
    const sortedSelected = selected.reduce((acc, curr) => {
      acc[newSelectedOrder.indexOf(curr.id)] = curr;
      return acc;
    }, []);

    this.batchGeoTable.updateRowDisplay(
      [...sortedSelected, ...notSelected],
      () => {},
    );
    return true;
  };

  BatchGeoTableMap.prototype.hideUnselectedRows = function () {
    this._getElementCache()
      .dataView.find("[data-id]:not(.selected)")
      .each(function (index, el) {
        $(el).toggleClass("hidden", true);
      });
  };

  BatchGeoTableMap.prototype.showUnselectedRows = function () {
    this._getElementCache()
      .dataView.find("[data-id].hidden")
      .each(function (index, el) {
        $(el).toggleClass("hidden", false);
      });
  };

  BatchGeoTableMap.prototype.getSelectedDisplayData = function () {
    return this.getDisplayDataBySelectedState().selected;
  };

  /**
   * Will update or reset the clusters based on the passed in array of Google
   * Marker instances. If clustering is turned off from the server or clarker
   * is never defined or there's only one marker passed it will just exit. If
   * no markers are passed it'll reset back to all markers.
   *
   * @param  {array} googleMarkers Array of Google Maps Markers
   * @return {object} Returns the BatchGeoTableMap instance
   */
  BatchGeoTableMap.prototype._updateOrResetClarkerMarkers = function () {
    if (!BatchGeoStore.getState().Cluster.enabled || !window.clarker)
      return this;
    window.clarker.checkClusters();

    return this;
  };

  /**
   * Preps the table resizer for table resizing events
   *
   * @return {object} Returns the BatchGeoTableMap instance
   */
  BatchGeoTableMap.prototype._initTableResizer = function () {
    var self = this;

    new window.BatchGeoDataAndTableResizer({
      container: this._cacheElements().dataView[0],
    });

    // Listen for map height changes to trigger CSS changes
    BatchGeoStore.addListener(
      function (state) {
        return state.BatchGeoTableMap.mapHeights;
      },
      function (newHeights, oldHeights) {
        return newHeights.mapWrap !== oldHeights.mapWrap;
      },
      function () {
        self.updateTableHeight();
      },
    );

    return this;
  };

  /**
   * Fits all given markers inside of the map
   *
   * @param {object} map    Your Google Map's map instance
   * @param {array} markers An array of markers to fit in the map
   */
  BatchGeoTableMap.prototype.fitMarkers = function (markers) {
    // If you pass in an empty array just bail out since we can't fitBounds
    if (!markers.length) return;

    var bounds = new google.maps.LatLngBounds();

    markers.forEach(function (marker) {
      bounds.extend(marker.getPosition());
    });

    this.map.fitBounds(bounds);

    return this;
  };

  /**
   * Gets Google Map markers based on an array of BatchGeoTable rows
   *
   * @param  {array} rowIds An array of BatchGeoTable rows
   * @return {array} a collection of Google markers
   */
  BatchGeoTableMap.prototype.getGoogleMarkersFromRows = function (rowIds) {
    // Some rows have matching markers set as null because the user given data
    // came in without any lat and lng and Google markers require a lat/lng.
    // So first we filter out rows that don't have a matching marker then we map
    // those BatchGeo markers to the Google markers.
    return rowIds
      .filter(function (id) {
        var m = this.markers[id];
        // check for the marker and if so, double check that it has coordinates
        return m ? BatchGeo.goodLatLon(m.Lat, m.Lon) : false;
      }, this)
      .map(function (id) {
        return this.markers[id].marker;
      }, this);
  };

  BatchGeoStore.addListener(
    "BatchGeoTableMap",
    "isTableSelectable",
    function (state) {
      if (state.isTableSelectable) {
        window.batchGeoTableMap._initSelectAllListener();
      } else {
        window.batchGeoTableMap.removeSelectAllListener();
      }
    },
  );

  window.BatchGeoTableMap = BatchGeoTableMap;
})(window);
